import React, { useCallback, useEffect, useState } from "react";

// Custom components
import ProfileInfo from "./ProfileInfo";

// UI components
import {
  alpha,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { useTheme } from "@emotion/react";
import useAuth from "src/hooks/useAuth";

import ProfileContextMenu from "src/layouts/dashboard/ProfileContextMenu";
import UserService from "src/services/User.service";

const DEFAULT_USER_PROFILE_IMAGE = "/assets/images/avatars/default_profile.jpg";
const DEFAULT_ORG_PROFILE_IMAGE = "/assets/images/avatars/default_profile.jpg";

function ProfileHeader({ user: passedUser, userId, newsCount }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user: loggedInUser } = useAuth();

  const [profile, setProfile] = useState(passedUser || null);
  const [open, setOpen] = useState(null);

  // Helper to set default profile picture
  const setDefaultProfilePic = (profileData) => {
    const updated = { ...profileData };
    if (!updated.profilePic && updated.userType === "user")
      updated.profilePic = DEFAULT_USER_PROFILE_IMAGE;
    else if (!updated.profilePic)
      updated.profilePic = DEFAULT_ORG_PROFILE_IMAGE;
    return updated;
  };

  // Fetch profile if userId is provided and no user object passed
  const fetchProfile = useCallback(async () => {
    if (!userId) return;
    const fetchedProfile = await UserService.fetchProfile({ _id: userId });
    setProfile(setDefaultProfilePic(fetchedProfile));
  }, [userId]);

  useEffect(() => {
    if (passedUser) {
      setProfile(setDefaultProfilePic(passedUser));
      return;
    }
    if (userId) {
      fetchProfile();
      return;
    }
    // No user or userId passed, fallback to logged-in user
    if (loggedInUser) {
      setProfile(setDefaultProfilePic(loggedInUser));
    }
  }, [passedUser, userId, loggedInUser, fetchProfile]);

  if (!profile) {
    return <Typography>Loading profile...</Typography>;
  }

  const isOwnProfile = loggedInUser?._id === profile._id;

  const handleMenuOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleMenuClose = () => {
    setOpen(null);
  };

  return (
    <Stack
      direction="column"
      justifyContent={isMobile ? "flex-end" : "center"}
      maxWidth="md"
      sx={{
        width: "100%",
        aspectRatio: isMobile ? "1" : "21/9",
        p: 4,
        pl: isMobile ? 4 : "45%",
        pb: isMobile ? 0 : 4,
        position: "relative",
        borderRadius: !isMobile && theme.shape.borderRadius / 30,
        overflow: "hidden",
        "&::before, &::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: {
            xs: "100%",
            md: "40%",
          },
          height: "100%",
        },
        "&::before": {
          backgroundImage: `url(${profile.profilePic})`,
          zIndex: -2,
          backgroundSize: "cover",
          backgroundPosition: "center",
        },

        "&::after": {
          zIndex: -1,
          backgroundImage: `linear-gradient(to ${
            isMobile ? "top" : "left"
          }, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))`,
        },
      }}
    >
      {newsCount && <ProfileInfo profile={{ news: newsCount }} />}
      <Typography variant="h3" textAlign={isMobile ? "center" : "start"}>
        {profile.name || "Anonymous"}
      </Typography>

      {isOwnProfile && (
        <>
          <IconButton
            sx={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 0.5,
            }}
            onClick={handleMenuOpen}
          >
            <MoreVertRoundedIcon />
          </IconButton>
          <ProfileContextMenu open={open} handleClose={handleMenuClose} />
        </>
      )}
    </Stack>
  );
}

export default ProfileHeader;
